import {TrustStatus} from '@sollapay/enums';
import {nodeColumn} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
  trustAccountBalanceColumn,
  trustAccountDateActionColumn,
  trustAccountNameColumn,
  trustAccountTypeColumn
} from './list-columns';
import {TrustAccountGroupedTable} from './TrustAccountGroupedTable';
import {TrustAccountListItemMenu} from './TrustAccountListItemMenu';
import {TrustAccountRoleBadge} from './TrustAccountRoleBadge';
import {TrustAccountSellersCell} from './TrustAccountSellersCell';
import {TrustAccountTeamCell} from './TrustAccountTeamCell';
import {
  TRUST_ACCOUNT_COLUMN_WIDTHS as WIDTHS,
  TRUST_ACCOUNT_TABLE_FRAME_CLASS,
  TRUST_ACCOUNT_TABLE_MIN_WIDTH
} from '../config';
import {getTrustAccountActionLabelKey, groupByTrustStatus} from '../utils';

import type {TrustAccountListColumn} from './list-columns';
import type {
  MemberTrustAccountListHandlers,
  MemberTrustAccountListItem,
  TrustAccountGroupFold
} from '../utils';
import type {ListOnSortingChange, ListSortingState} from '@sollapay/ui/components';
import type {TFunction} from 'i18next';
import type {FC} from 'react';

type Row = MemberTrustAccountListItem;

export interface TrustAccountTableProps extends MemberTrustAccountListHandlers {
  items: Row[];
  sorting: ListSortingState;
  onSortingChange: ListOnSortingChange;
  /** Pins the caller's role to each tile — for the "all" tab, where rows of every role mix. */
  showRoleBadge?: boolean;
  groupFold: TrustAccountGroupFold;
}

/** Rows are grouped by status and use `rowLink`, so the cells' own buttons aren't nested in a `role="button"` row. */
export const TrustAccountTable: FC<TrustAccountTableProps> = ({
  items,
  sorting,
  onSortingChange,
  showRoleBadge = false,
  groupFold,
  onEditAccount,
  onDeleteAccount,
  onAccountAction,
  onManageTeam
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const groups = useMemo(() => groupByTrustStatus(items), [items]);
  const columns = useMemo(
    () =>
      buildColumns(t, showRoleBadge, {
        onEditAccount,
        onDeleteAccount,
        onAccountAction,
        onManageTeam
      }),
    [t, showRoleBadge, onEditAccount, onDeleteAccount, onAccountAction, onManageTeam]
  );

  return (
    <div data-slot="trust-account-table" className={TRUST_ACCOUNT_TABLE_FRAME_CLASS}>
      <TrustAccountGroupedTable
        variant="embedded"
        columns={columns}
        groups={groups}
        groupFold={groupFold}
        sorting={sorting}
        onSortingChange={onSortingChange}
        rowLink
        minWidth={TRUST_ACCOUNT_TABLE_MIN_WIDTH}
        emptyStateText={t('common:noResults')}
      />
    </div>
  );
};

function buildColumns(
  t: TFunction,
  showRoleBadge: boolean,
  handlers: MemberTrustAccountListHandlers
): TrustAccountListColumn<Row>[] {
  const {onEditAccount, onDeleteAccount, onAccountAction, onManageTeam} = handlers;

  return [
    trustAccountNameColumn<Row>({
      header: t('columns.trustName'),
      width: WIDTHS.trustName,
      renderRoleBadge: showRoleBadge
        ? row => <TrustAccountRoleBadge accessRole={row.role} />
        : undefined
    }),
    trustAccountBalanceColumn<Row>({header: t('columns.balance'), width: WIDTHS.balance}),
    trustAccountTypeColumn<Row>({header: t('columns.type'), width: WIDTHS.type}),
    nodeColumn<Row>({
      id: 'team',
      header: t('columns.team'),
      width: WIDTHS.team,
      enableSorting: false,
      cell: row => (
        <TrustAccountTeamCell
          trustee={row.trustee}
          team={row.team}
          onManageTeam={() => onManageTeam?.(row)}
        />
      )
    }),
    nodeColumn<Row>({
      id: 'developer',
      header: t('columns.seller'),
      width: WIDTHS.developer,
      accessorFn: row => row.sellers[0]?.fullName ?? '',
      cell: row => (
        <TrustAccountSellersCell
          sellers={row.sellers}
          buyersCount={row.buyersCount}
          showBuyers={row.trustStatus === TrustStatus.TRUST_ACTIVE}
          onOpenBuyers={() => onAccountAction?.(row)}
        />
      )
    }),
    trustAccountDateActionColumn<Row>({
      id: 'createdAt',
      header: t('columns.date'),
      width: WIDTHS.createdAt,
      getCtaLabel: row =>
        t(`common:${getTrustAccountActionLabelKey(row.trustStatus, row.permissions.canManage)}`),
      onAction: row => onAccountAction?.(row),
      renderMenu: row => (
        <TrustAccountListItemMenu
          item={row}
          onEditAccount={onEditAccount}
          onDeleteAccount={onDeleteAccount}
        />
      )
    })
  ];
}
